import { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Body2 from 'components/Body2';
import Body3 from 'components/Body3';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Phone from 'components/Phone';
import Text from 'components/Text';
import useIntersectionObserver from 'components/hooks/useIntersectionObserver';

import phoneContext from 'contexts/phone';
import purlAgentContext from 'contexts/purlAgentContext';

import formatPhoneNumber from 'utilities/formatPhoneNumber';
import {
    CONTACT_AN_AGENT_ROUTE,
    PLANS_ROUTE,
    REQUEST_CALL_BACK
} from 'utilities/routes';
import {
    CONTACT_CALLBACK_KEY,
    REQUEST_CALLBACK_KEY
} from 'utilities/storageKeys';

import close from './close.svg';
import crossCircle from './crossCircle.svg';
import image from './image.svg';

import styles from './styles.module.scss';

import { set } from 'temp/utilities/storage';

const INTERSECTION_RATIO_PLANS_SIDE_HEADER = 0.5;

const AgentHelp = () => {
    const { purlAgentData } = useContext(purlAgentContext);
    const { agentFirstName, virtualNumber } = purlAgentData || {};
    const { phone } = useContext(phoneContext);
    const { text = '', number } = phone;

    const history = useHistory();

    const [isTextOpen, setIsTextOpen] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const isPlansPage = window.location.pathname === PLANS_ROUTE;
    const { intersectionRatio } = useIntersectionObserver({
        root: null,
        threshold: INTERSECTION_RATIO_PLANS_SIDE_HEADER,
        target: isPlansPage ? '#plansHeader' : null
    });
    const isPlansHeaderVisible =
        intersectionRatio <= INTERSECTION_RATIO_PLANS_SIDE_HEADER;

    const phoneNumber = purlAgentData
        ? formatPhoneNumber(virtualNumber?.substring(2))
        : text;
    const phoneNum = purlAgentData
        ? '1-' + formatPhoneNumber(virtualNumber?.substring(2))
        : number;

    const handleClose = () => {
        setIsTextOpen(false);
    };

    const handleToggleExpand = () => {
        handleClose();
        setIsExpanded(val => !val);
    };

    const handleReset = () => {
        setIsTextOpen(true);
        setIsExpanded(false);
    };

    useEffect(() => {
        if (isPlansHeaderVisible) {
            setIsExpanded(false);
        }
    }, [isPlansHeaderVisible]);

    const handleReachAgent = () => {
        if (purlAgentData) {
            set(REQUEST_CALLBACK_KEY, 'true');
            history.push(REQUEST_CALL_BACK);
        } else {
            set(CONTACT_CALLBACK_KEY, 'true');
            history.push(CONTACT_AN_AGENT_ROUTE);
        }
    };

    return (
        <>
            <div
                className={`${styles.helpDescription} ${
                    isExpanded ? styles.helpDescriptionIsExpanded : ''
                }`}
            >
                <div className={styles.helpDescriptionHeader}>
                    <Body2
                        text="Need help making a decision?"
                        onClick={handleReset}
                        className={`${styles.blueColor} ${styles.headerHelpText}`}
                    />

                    <Icon
                        alt="White and blue message boxes"
                        className={styles.helpDescriptionIcon}
                        image={image}
                        onClick={handleToggleExpand}
                    />
                </div>

                <div className={styles.helpDescriptionBody}>
                    <Text
                        text="Get personalized guidance"
                        className={styles.bodyHeading}
                    />

                    <Body2
                        text="Connect with a licensed insurance agent."
                        className={styles.bodyText}
                    />

                    <Phone
                        className={styles.phoneHeading}
                        number={phoneNum}
                        text={phoneNumber}
                    />

                    <Body2
                        text="Or have an agent contact you."
                        className={styles.bodyFooterText}
                    />
                    <Button
                        label={
                            purlAgentData
                                ? `Reach ${agentFirstName}`
                                : 'Reach an Agent'
                        }
                        className={styles.reachButton}
                        onClick={handleReachAgent}
                    />
                </div>
            </div>
            <div className={`${styles.agentHelp}`}>
                <div
                    className={`${styles.contentIconContainer} ${
                        isTextOpen ? '' : styles.contentIconContainerClosed
                    }`}
                >
                    <Icon
                        alt="An X with a blue circle around it"
                        className={styles.crossIcon}
                        image={close}
                        onClick={handleClose}
                    />
                    <div
                        className={styles.contentContainer}
                        onClick={handleToggleExpand}
                    >
                        <Body3 text="Need help making a decision?" />
                    </div>
                </div>
                {!isExpanded && (
                    <Icon
                        alt="White and blue message boxes"
                        className={styles.image}
                        image={image}
                        onClick={handleToggleExpand}
                    />
                )}
                <div
                    className={`${styles.crossCircleContainer} ${
                        isExpanded ? styles.crossCircleContainerVisible : ''
                    }`}
                >
                    <Icon
                        alt="An X with a blue circle around it"
                        image={crossCircle}
                        className={`${styles.crossCircle} ${
                            isExpanded ? styles.crossCircleVisible : ''
                        }`}
                        onClick={handleToggleExpand}
                    />
                </div>
            </div>
        </>
    );
};

export default AgentHelp;
